/**
 * Billing Transaction – Receipt Bills.
 * GET /histopathology/billing/reports/receipt/?from_date&to_date[&export=pdf]
 * Response totals: total_receipt, total_amount, cash/card/upi/bank/cheque_amount, net_total.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 2, py: 1.2, minWidth: "9vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1vw", fontWeight: 700, color: "#1a1a1a" }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.72vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function ReceiptBills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({
    count: 0, total: 0, cash: 0, card: 0, upi: 0, bank: 0, cheque: 0, net: 0,
  });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_RECEIPT_BILLS}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          setTotals({
            count: res.data.total_receipt ?? 0,
            total: +res.data.total_amount || 0,
            cash: +res.data.cash_amount || 0,
            card: +res.data.card_amount || 0,
            upi: +res.data.upi_amount || 0,
            bank: +res.data.bank_amount || 0,
            cheque: +res.data.cheque_amount || 0,
            net: +res.data.net_total || 0,
          });
        } else {
          setRows([]);
          setTotals({ count: 0, total: 0, cash: 0, card: 0, upi: 0, bank: 0, cheque: 0, net: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setTotals({ count: 0, total: 0, cash: 0, card: 0, upi: 0, bank: 0, cheque: 0, net: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.bill_number, r.patient_name, r.nxt_id, r.created_by, r.payment_modes, r.corporate_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_RECEIPT_BILLS}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const handlePrint = (row) => {
    const billId = row?.id ?? row?.bill_id;
    const nxtId = row?.nxt_id;
    if (!billId && !nxtId) {
      setMsg({ err: true, errText: "Bill ID missing for receipt print.", ok: false, okText: "" });
      return;
    }
    const params = new URLSearchParams();
    if (billId) params.append("bill_id", String(billId));
    else params.append("nxt_id", String(nxtId));
    const url = `${Serviceurls.HISTOPATHOLOGY_RECEIPT_PRINT}?${params.toString()}`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch((err) => setMsg({ err: true, errText: err?.response?.data?.message || err?.message || "Unable to print receipt.", ok: false, okText: "" }));
  };

  const columns = [
    {
      field: "receipt_details", headerName: "Receipt Details", flex: 1.2, minWidth: 170, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.bill_number || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.receipt_date
              ? p.row.receipt_date
              : (p.row.invoice_date
                  ? p.row.invoice_date
                  : p.row.created_date ? DateTime.fromISO(p.row.created_date).toFormat("dd-MM-yyyy hh:mm a") : "-")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "patient_details", headerName: "Patient Details", flex: 1.2, minWidth: 170, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.patient_name || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.nxt_id || "-"}</Typography>
        </Box>
      ),
    },
    { field: "bill_number", headerName: "Bill No.", flex: 1, minWidth: 140 },
    {
      field: "receipt_for", headerName: "Receipt For", flex: 1, minWidth: 130,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">Bill Receipt</Typography>
          {+p.row.pending_amount > 0 && p.row.received_amount > 0 && (
            <Typography variant="caption" color="text.secondary">Against Credit</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "payment_details", headerName: `Payment Details (${CurrencySymbol})`, flex: 1.1, minWidth: 150,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>
            {CommonValidation.formatAmount(p.row.amount_received ?? p.row.received_amount ?? 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">{p.row.payment_modes || "-"}</Typography>
        </Box>
      ),
    },
    { field: "created_by", headerName: "Collected By", flex: 0.9, minWidth: 110 },
    {
      field: "action", headerName: "Action", sortable: false, flex: 0.6, minWidth: 80,
      align: "center", headerAlign: "center",
      renderCell: (p) => (
        <Tooltip title="Print">
          <IconButton size="small" onClick={() => handlePrint(p.row)}>
            <PrintOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
        <SummaryCard label="No of Receipt" value={totals.count} />
        <SummaryCard label="Received Amount" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.total)}`} />
        <SummaryCard label="Cash" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.cash)}`} />
        <SummaryCard label="Card" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.card)}`} />
        <SummaryCard label="UPI" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.upi)}`} />
        <SummaryCard label="Bank" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.bank)}`} />
        <SummaryCard label="Cheque" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.cheque)}`} />
        <SummaryCard label="Net Total" value={`${CurrencySymbol} ${CommonValidation.formatAmount(totals.net)}`} />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "12vw" }}>
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            HideAllMenu
            removeMarginRight
            SelectedDates={(from, to, idx) => {
              setFromDate(from);
              setToDate(to);
              setDateIndex(idx);
            }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "14vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={56}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
